import { getFileMimeType } from './getFileMimeType';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';

const BLOB_URL_REVOKE_DELAY_MS = 10000;

export const appendTimestamp = (name: string) => {
  const dotIndex = name.lastIndexOf('.');
  if (dotIndex === -1) {
    return `${name} - ${new Date().getTime()}`;
  }
  return `${name.slice(0, dotIndex)} - ${new Date().getTime()}${name.slice(
    dotIndex
  )}`;
};

export const base64ToBlobUrl = (base64: string, mimeType: string): string => {
  const byteCharacters = atob(base64);
  const byteArrays = [];
  const CHUNK_SIZE = 512;

  for (let offset = 0; offset < byteCharacters.length; offset += CHUNK_SIZE) {
    const slice = byteCharacters.slice(offset, offset + CHUNK_SIZE);
    const byteNumbers = new Array(slice.length);
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }

  const blob = new Blob(byteArrays, { type: mimeType });
  return URL.createObjectURL(blob);
};

/**
 * Shows a temporary overlay with a "tap to save" button on iOS.
 * This is used when navigator.share() fails because the user activation
 * expired (e.g. after an async API call). The button creates a fresh
 * user activation so navigator.share() will succeed.
 */
const showIOSShareRetryOverlay = (file: File): Promise<void> => {
  return new Promise((resolve) => {
    const overlay = document.createElement('div');
    overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.5);
      z-index: 9999;
      display: flex;
      align-items: flex-end;
      justify-content: center;
      padding-bottom: env(safe-area-inset-bottom, 40px);
      animation: fadeIn 0.2s ease-out;
    `;

    const buttonContainer = document.createElement('div');
    buttonContainer.style.cssText = `
      background: white;
      border-radius: 16px;
      padding: 20px 24px;
      margin: 0 16px 24px 16px;
      width: calc(100% - 32px);
      max-width: 400px;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
      text-align: center;
      direction: rtl;
    `;

    const title = document.createElement('div');
    title.textContent = Dictionary[DICTIONARY.General].tapToSaveFile;
    title.style.cssText = `
      font-size: 17px;
      font-weight: 600;
      color: #1c1c1e;
      margin-bottom: 16px;
    `;

    const saveButton = document.createElement('button');
    saveButton.textContent = Dictionary[DICTIONARY.General].saveFile;
    saveButton.style.cssText = `
      width: 100%;
      padding: 14px;
      font-size: 17px;
      font-weight: 600;
      border-radius: 12px;
      border: none;
      background: #007aff;
      color: white;
      cursor: pointer;
      margin-bottom: 8px;
      -webkit-tap-highlight-color: transparent;
    `;

    const cancelButton = document.createElement('button');
    cancelButton.textContent = Dictionary[DICTIONARY.General].close;
    cancelButton.style.cssText = `
      width: 100%;
      padding: 14px;
      font-size: 17px;
      font-weight: 400;
      border-radius: 12px;
      border: none;
      background: #f2f2f7;
      color: #007aff;
      cursor: pointer;
      -webkit-tap-highlight-color: transparent;
    `;

    const cleanup = () => {
      if (document.body.contains(overlay)) {
        document.body.removeChild(overlay);
      }
      resolve();
    };

    saveButton.onclick = async () => {
      try {
        await navigator.share({ files: [file] });
      } catch (e) {
        if ((e as Error)?.name !== 'AbortError') {
          console.error('Error sharing file on retry:', e);
        }
      }
      cleanup();
    };

    cancelButton.onclick = cleanup;

    overlay.onclick = (e) => {
      if (e.target === overlay) cleanup();
    };

    buttonContainer.appendChild(title);
    buttonContainer.appendChild(saveButton);
    buttonContainer.appendChild(cancelButton);
    overlay.appendChild(buttonContainer);
    document.body.appendChild(overlay);
  });
};

export const downloadFile = async (
  base64: string,
  fileExtension: string,
  fileName: string,
  openPdfInNewTab: boolean = true
) => {
  if (!base64) {
    alert(Dictionary[DICTIONARY.General].downloadFileError);
    return;
  }

  try {
    const mimeType = getFileMimeType(fileExtension);
    const blobUrl = base64ToBlobUrl(base64, mimeType);

    // Ensure filename has extension - critical for iOS to open the file correctly.
    // Without extension, iOS doesn't know which app to use.
    const ext = fileExtension?.startsWith('.')
      ? fileExtension
      : fileExtension
      ? `.${fileExtension}`
      : '';
    const nameWithExtension =
      ext && fileName && !fileName.toLowerCase().endsWith(ext.toLowerCase())
        ? `${fileName}${ext}`
        : fileName || `document${ext}`;
    const timestampedFileName = appendTimestamp(nameWithExtension);

    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

    if (isIOS) {
      // Create blob from base64
      const byteCharacters = atob(base64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: mimeType });
      const file = new File([blob], timestampedFileName, { type: mimeType });

      // iOS Safari/Chrome don't support the download attribute on <a> tags.
      // Use the Web Share API which opens the native share sheet.
      if (navigator.share) {
        try {
          await navigator.share({ files: [file] });
          return;
        } catch (error) {
          if ((error as Error)?.name === 'AbortError') return;

          // NotAllowedError = user activation expired (async API call took too long).
          // Show a retry button that gives navigator.share() a fresh user activation.
          await showIOSShareRetryOverlay(file);
          return;
        }
      }

      // Last resort for very old iOS without Web Share API
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), BLOB_URL_REVOKE_DELAY_MS);
    } else {
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = timestampedFileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  } catch (error) {
    console.error('Error downloading file:', error);
    alert(Dictionary[DICTIONARY.General].downloadFileError);
  }
};

export const downloadAudio = async (audioSrc: string, name: string) => {
  try {
    const response = await fetch(audioSrc, {
      method: 'GET',
      headers: {
        Accept: 'audio/mpeg',
      },
    });
    if (!response.ok) {
      throw new Error('Failed to fetch audio file');
    }
    const blob = await response.blob();

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = appendTimestamp(`${name}.mp3`);
    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error downloading audio:', error);
    alert('שגיאה בהורדת הקובץ. אנא נסה שוב.');
  }
};
